/**
 * One card of the contact band: a coloured tile with short bold text and a round arrow button.
 * Used by `../Desktop/ContactUs.tsx` and `../Mobile/ContactUs.tsx`.
 *
 * What a click does depends on `buttonLink`:
 * - non-empty: the whole card is a `Link` to `buttonLink`, split at `#` into path and hash (so
 *   `/contact-us#quoteRequest` preselects the quote topic on the contact form). With `isCareers`
 *   the link opens in a new tab as a plain (non-Next) link; the layouts pass the
 *   `NEXT_PUBLIC_CAREERS_URL_USA` / `_CANADA` values as `buttonLink` then.
 * - empty: the card opens the live-chat widget by calling `window.LiveChatWidget.call("maximize")`.
 *   No file in this repo loads that widget, and when it is absent the click does nothing. On
 *   desktop both the tile and the button open the chat; below `lg` only the button does.
 */
import React from "react";
import styled from "@emotion/styled";
import { Media } from "@ryerson/frontend.next";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { Button } from "@ryerson/frontend.button";
import { Link } from "@ryerson/frontend.navigation";

/** Size of the card's round arrow `Button`. */
export type ButtonSize = "xs" | "sm" | "md" | "lg";
/**
 * The arrow button's `foreground` (icon colour scheme) on `@ryerson/frontend.button` →
 * `Button`.
 */
export type Foreground = "light" | "dark";

interface ContactCardProps {
	/** Tile background. Default: theme white. */
	color?: string;
	/** The card's text. */
	description?: string;
	/** Default: theme `darkerGray`. */
	textColor?: string;
	/** Arrow button background. Default: theme `primaryBrand`. */
	buttonColor?: string;
	foreground?: Foreground;
	buttonSize?: ButtonSize;
	/**
	 * Feeds an `&:nth-of-type(2)` top-margin rule (40px, 20px below `lg`) that never matches,
	 * because each `Card` is the only child of its `Media` wrapper or `Link`; the layouts space the
	 * cards themselves.
	 */
	withImage?: boolean;
	/** Below `lg`: a shorter tile with text and button side by side. */
	oneButtonVariant?: boolean;
	/** `path#hash` target. Empty means the card opens the live chat. */
	buttonLink?: string;
	/** Open `buttonLink` in a new tab as a plain (non-Next) link. */
	isCareers?: boolean;
}

/** One contact card; see the file header for what a click does. */
const ContactCard: React.FC<ContactCardProps> = ({
	color,
	description = "",
	textColor,
	buttonColor,
	foreground = "light",
	buttonSize = "xs",
	withImage = true,
	oneButtonVariant = false,
	buttonLink = "",
	isCareers = false,
}) => {
	const { theme } = useTheme();

	if (!color) {
		color = `${theme.colors.primary.white}`;
	}

	if (!textColor) {
		textColor = `${theme.colors.primary.darkerGray}`;
	}

	if (!buttonColor) {
		buttonColor = `${theme.colors.primary.primaryBrand}`;
	}

	// A new styled component on every render, built from this card's `color`, `withImage` and
	// `oneButtonVariant`.
	const Card = styled(Flex)`
		background-color: ${color};
		border-radius: 2px;
		min-height: 190px;
		cursor: pointer;
		padding: 40px;

		&:nth-of-type(2) {
			margin-top: ${withImage ? "40px" : "0px"};
		}

		@media only screen and (max-width: ${theme.breakpoints.lg}) {
			min-height: ${oneButtonVariant ? "71px" : "120px"};
			max-height: 120px;
			&:nth-of-type(2) {
				margin-top: ${withImage ? "20px" : "0px"};
			}
			padding: 20px;
		}
	`;

	return (
		<>
			<Media greaterThanOrEqual="lg">
				{buttonLink && buttonLink.length > 0 ? (
					<Link
						to={buttonLink.split("#")[0]}
						hash={buttonLink.split("#")[1]}
						target={isCareers ? "_blank" : "_self"}
						next={!isCareers}
					>
						<Card direction="column" justifyContent="space-between">
							<FlexItem css={{ width: "115px" }}>
								<Typography
									css={{ margin: "0" }}
									variant="p"
									size="md"
									weight="bold"
									color={textColor}
								>
									{description}
								</Typography>
							</FlexItem>
							<Flex justifyContent="flex-end">
								<Button
									shape="circle"
									label=""
									rightIcon="forward"
									size={buttonSize}
									onClick={() => {}}
									background={buttonColor}
									foreground={foreground}
									dataTestId="button"
								/>
							</Flex>
						</Card>
					</Link>
				) : (
					<Card
						onClick={() => {
							if (window && (window as any).LiveChatWidget)
								(window as any).LiveChatWidget.call("maximize");
						}}
						direction="column"
						justifyContent="space-between"
						data-test-id="contact-us-chat-with-metal-expert-card"
					>
						<FlexItem css={{ width: "115px" }}>
							<Typography
								css={{ margin: "0" }}
								variant="p"
								weight="bold"
								size="md"
								color={textColor}
							>
								{description}
							</Typography>
						</FlexItem>
						<Flex justifyContent="flex-end">
							<Button
								shape="circle"
								label=""
								rightIcon="forward"
								size={buttonSize}
								onClick={() => {
									if (window && (window as any).LiveChatWidget)
										(window as any).LiveChatWidget.call("maximize");
								}}
								background={buttonColor}
								foreground={foreground}
								dataTestId="chat-with-metal-expert-button"
							/>
						</Flex>
					</Card>
				)}
			</Media>
			<Media lessThan="lg">
				{buttonLink && buttonLink.length > 0 ? (
					<Link
						to={buttonLink.split("#")[0]}
						hash={buttonLink.split("#")[1]}
						target={isCareers ? "_blank" : "_self"}
						next={!isCareers}
					>
						<Card
							onClick={() => {}}
							direction={oneButtonVariant ? "row" : "column"}
							justifyContent="space-between"
							data-test-id="contact-us-card"
						>
							<FlexItem css={{ width: "115px" }}>
								<Typography
									css={{ margin: "0" }}
									variant="p"
									size="md"
									weight="bold"
									color={textColor}
								>
									{description}
								</Typography>
							</FlexItem>
							<Flex
								direction={oneButtonVariant ? "column" : "row"}
								justifyContent={oneButtonVariant ? "space-around" : "flex-end"}
							>
								<Button
									shape="circle"
									label=""
									rightIcon="forward"
									size={buttonSize}
									onClick={() => {}}
									background={buttonColor}
									foreground={foreground}
									dataTestId="button"
								/>
							</Flex>
						</Card>
					</Link>
				) : (
					<Card
						onClick={() => {}}
						direction={oneButtonVariant ? "row" : "column"}
						justifyContent="space-between"
						data-test-id="contact-us-card"
					>
						<FlexItem css={{ width: "115px" }}>
							<Typography
								css={{ margin: "0" }}
								variant="p"
								size="md"
								weight="bold"
								color={textColor}
							>
								{description}
							</Typography>
						</FlexItem>
						<Flex
							direction={oneButtonVariant ? "column" : "row"}
							justifyContent={oneButtonVariant ? "space-around" : "flex-end"}
						>
							<Button
								shape="circle"
								label=""
								rightIcon="forward"
								size={buttonSize}
								onClick={() => {
									if (window && (window as any).LiveChatWidget)
										(window as any).LiveChatWidget.call("maximize");
								}}
								background={buttonColor}
								foreground={foreground}
								dataTestId="button"
							/>
						</Flex>
					</Card>
				)}
			</Media>
		</>
	);
};

export default ContactCard;
